import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { Grid, Header, Icon, Message, Popup, Segment } from "semantic-ui-react";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import { partyIsTitleCompany } from "../../../app/common/util/util";
import usePartyTransactionsByStatus, {
  toJsDate,
} from "../partyAllOverview/usePartyTransactionsByStatus";
import PartyTransactionsTable from "../partyAllOverview/PartyTransactionsTable";
import PartyNewDocsTable from "./PartyNewDocsTable";

// documentName (history's "document" field) for the purchase-offer contract,
// by state. Colorado has several dated/typed variants (Residential,
// Land, Commercial, Income Residential, Foreclosure) that all start with
// "Contract to Buy and Sell"; the other states below are matched exactly.
// TODO: confirm the exact TX/CA/LA/OK names against real documents - these
// are best guesses from the form template catalog, not yet verified.
const OTHER_STATE_PURCHASE_OFFER_NAMES = [
  "One to Four Family Residential Contract", // Texas
  "Residential Purchase Agreement", // California
  "Residential Agreement to Buy or Sell", // Louisiana
  "GTAR Sales Contract", // Oklahoma
];
function isPurchaseOfferDocumentName(name) {
  if (!name) return false;
  return (
    name.startsWith("Contract to Buy and Sell") ||
    OTHER_STATE_PURCHASE_OFFER_NAMES.includes(name)
  );
}

// Builds a party list (newest share first) from "shared" history entries -
// one entry per transaction, keyed off that transaction's most recent
// matching share. Pass matchesDocumentName to restrict to specific documents
// (e.g. purchase offers); omit it to match any shared document.
function buildRecentlySharedTransactions({
  historyEntries,
  partiesForDisplay,
  since,
  until,
  matchesDocumentName,
}) {
  const latestShareByTransactionId = new Map();
  (historyEntries || []).forEach((entry) => {
    if (matchesDocumentName && !matchesDocumentName(entry.document)) return;
    const sharedAt = toJsDate(entry.createdAt);
    if (!sharedAt || sharedAt < since || sharedAt > until) return;
    const existing = latestShareByTransactionId.get(entry.transactionId);
    if (!existing || sharedAt > existing) {
      latestShareByTransactionId.set(entry.transactionId, sharedAt);
    }
  });

  return Array.from(latestShareByTransactionId.entries())
    .map(([transactionId, sharedAt]) => {
      const party = (partiesForDisplay || []).find(
        (p) => p?.transactionId === transactionId
      );
      return party ? { party, sharedAt } : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.sharedAt - a.sharedAt)
    .map((entry) => entry.party);
}

// Like buildRecentlySharedTransactions, but keyed by transactionId+document
// name instead of collapsed to one-per-transaction - New Docs lists every
// individual document, not just the transactions that got one.
function buildRecentlySharedDocEntries({ historyEntries, since, until }) {
  const latestShareByKey = new Map();
  (historyEntries || []).forEach((entry) => {
    if (!entry.document || !entry.transactionId) return;
    const sharedAt = toJsDate(entry.createdAt);
    if (!sharedAt || sharedAt < since || sharedAt > until) return;
    const key = `${entry.transactionId}::${entry.document}`;
    const existing = latestShareByKey.get(key);
    if (!existing || sharedAt > existing.sharedAt) {
      latestShareByKey.set(key, {
        transactionId: entry.transactionId,
        documentName: entry.document,
        sharedAt,
      });
    }
  });
  return Array.from(latestShareByKey.values());
}

export default function PartyDashboard() {
  const { sharedDocHistoryForUser, sharedDocsForUser, transactionsForUser } =
    useSelector((state) => state.party);
  const {
    loaded,
    currentUserProfile,
    parties,
    partiesForDisplay,
    completedTransactions,
  } = usePartyTransactionsByStatus();
  const [selectedKey, setSelectedKey] = useState("closingSoon");

  if (!loaded) return <LoadingComponent content="Loading details..." />;

  // This dashboard is only for title company users (and their assistant,
  // who logs into the same party record - see fetchPartiesForUserFromDb's
  // assistant.email match). Anyone else who lands on this route gets sent
  // back to the transaction list.
  const isTitleCompanyUser = (parties || []).some((party) =>
    partyIsTitleCompany(party.role)
  );
  if (!isTitleCompanyUser) {
    return <Navigate to="/overviewParty" replace />;
  }

  const now = new Date();
  const sevenDaysOut = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  // Every document (not deduped to one-per-transaction) shared with this
  // user in the last 7 days.
  const recentlySharedDocEntries = buildRecentlySharedDocEntries({
    historyEntries: sharedDocHistoryForUser,
    since: sevenDaysAgo,
    until: now,
  });

  const needsAttentionTransactions = (partiesForDisplay || []).filter(
    (party) => party.needsSignatures
  );

  const closingSoonTransactions = (partiesForDisplay || [])
    .filter((party) => {
      const closingDate = toJsDate(party.closingDateTime);
      return closingDate && closingDate >= now && closingDate <= sevenDaysOut;
    })
    .sort(
      (a, b) => toJsDate(a.closingDateTime) - toJsDate(b.closingDateTime)
    );

  // "New Orders" = transactions where a purchase-offer contract was shared
  // with this user in the last 7 days. Recency is per-transaction: if a
  // transaction has multiple matching shares, only its most recent counts.
  const newOrdersTransactions = buildRecentlySharedTransactions({
    historyEntries: sharedDocHistoryForUser,
    partiesForDisplay,
    since: sevenDaysAgo,
    until: now,
    matchesDocumentName: isPurchaseOfferDocumentName,
  });

  // "New Docs" = individual documents shared with this user (or their
  // assistant - same party-portal login) in the last 7 days.
  // TODO: this used to also exclude docs already viewed/downloaded (via a
  // history query keyed off these candidate transaction ids), but that
  // dependent-query pattern was pulled after it caused the page to keep
  // reloading - re-add once that's built more robustly.
  const newDocsItems = recentlySharedDocEntries
    .map((entry) => {
      const party = (partiesForDisplay || []).find(
        (p) => p?.transactionId === entry.transactionId
      );
      const doc = (sharedDocsForUser || []).find(
        (d) =>
          d.transactionId === entry.transactionId &&
          d.name === entry.documentName
      );
      const transaction = (transactionsForUser || []).find(
        (t) => t.id === entry.transactionId
      );
      return party && doc
        ? { doc, party, transaction, sharedAt: entry.sharedAt }
        : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.sharedAt - a.sharedAt);

  const closedThisMonthTransactions = completedTransactions.filter(
    (party) => {
      const closedDate =
        toJsDate(party.closingDateTime) || toJsDate(party.updatedAt);
      return (
        closedDate &&
        closedDate.getFullYear() === now.getFullYear() &&
        closedDate.getMonth() === now.getMonth()
      );
    }
  );

  const overviewStats = [
    {
      key: "needsAttention",
      label: "Needs Attention",
      transactions: needsAttentionTransactions,
      caption: "transactions",
      icon: "exclamation circle",
      color: "#d9534f",
      showClosingDate: false,
    },
    {
      key: "closingSoon",
      label: "Closing Soon",
      transactions: closingSoonTransactions,
      caption: "next 7 days",
      icon: "calendar alternate outline",
      color: "#f0ad4e",
      showClosingDate: true,
      showNeedsSignatures: false,
    },
    {
      key: "newOrders",
      label: "New Orders",
      transactions: newOrdersTransactions,
      caption: "last 7 days",
      icon: "file alternate outline",
      color: "#2ecc71",
      showClosingDate: false,
      showNeedsSignatures: false,
    },
    {
      key: "newDocs",
      label: "New Docs",
      items: newDocsItems,
      isDocsTable: true,
      caption: "last 7 days",
      icon: "copy outline",
      color: "#5bc0de",
      description:
        "Documents shared with you (or your assistant) in the last 7 days.",
    },
    {
      key: "tasks",
      label: "Tasks",
      transactions: [],
      caption: "Coming Soon",
      icon: "tasks",
      color: "#5cb85c",
      showClosingDate: false,
      comingSoon: true,
      hidden: true, // TODO: unhide once Tasks is ready
    },
    {
      key: "closedThisMonth",
      label: "Closed This Month",
      transactions: closedThisMonthTransactions,
      caption: "transactions",
      icon: "check circle outline",
      color: "#8e44ad",
      showClosingDate: true,
      hidden: true, // TODO: unhide once ready
    },
  ];

  const visibleStats = overviewStats.filter((stat) => !stat.hidden);
  const selectedStat = visibleStats.find((stat) => stat.key === selectedKey);

  function handleCardClick(key) {
    setSelectedKey((current) => (current === key ? null : key));
  }

  return (
    <div className="main-page-wrapper">
      <Grid stackable className="small bottom margin">
        {currentUserProfile?.firstName?.length > 1 && (
          <Grid.Column computer={16} className="small bottom margin">
            <Header size="medium">
              Good day, {currentUserProfile?.firstName}
            </Header>
          </Grid.Column>
        )}
        <Grid.Column computer={16}>
          <Grid stackable columns={visibleStats.length} doubling>
            {visibleStats.map((stat) => {
              const isSelected = selectedKey === stat.key;
              const card = (
                <Segment
                  onClick={() => handleCardClick(stat.key)}
                  style={{
                    cursor: "pointer",
                    borderRadius: "8px",
                    padding: "16px",
                    height: "100%",
                    margin: 0,
                    border: isSelected
                      ? `2px solid ${stat.color}`
                      : "1px solid rgba(34,36,38,.1)",
                    boxShadow: isSelected
                      ? "0 2px 8px rgba(0,0,0,.12)"
                      : "none",
                  }}
                >
                  <Header
                    as="h4"
                    style={{
                      margin: "0 0 12px",
                      color: "rgba(0,0,0,.85)",
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                      fontSize: "0.95em",
                    }}
                  >
                    {stat.label}
                  </Header>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "14px",
                    }}
                  >
                    <Icon
                      name={stat.icon}
                      size="big"
                      style={{ color: stat.color, margin: 0 }}
                    />
                    <div
                      style={{
                        fontSize: "2em",
                        fontWeight: "bold",
                        lineHeight: 1,
                      }}
                    >
                      {stat.comingSoon
                        ? "–"
                        : (stat.isDocsTable ? stat.items : stat.transactions)
                            .length}
                    </div>
                  </div>
                  <div
                    style={{
                      color: "rgba(0,0,0,.5)",
                      fontSize: "0.85em",
                      marginTop: "8px",
                    }}
                  >
                    {stat.caption}
                  </div>
                </Segment>
              );
              return (
                <Grid.Column key={stat.key}>
                  {stat.description ? (
                    <Popup
                      trigger={card}
                      content={stat.description}
                      position="top center"
                      wide
                    />
                  ) : (
                    card
                  )}
                </Grid.Column>
              );
            })}
          </Grid>
        </Grid.Column>
        {selectedStat && (
          <Grid.Column computer={16} className="medium top margin">
            {selectedStat.comingSoon ? (
              <Message icon color="blue">
                <Icon name="clock outline" />
                <Message.Content>
                  <Message.Header>Coming Soon</Message.Header>
                  {selectedStat.label} is not available yet.
                </Message.Content>
              </Message>
            ) : selectedStat.isDocsTable ? (
              selectedStat.items.length > 0 ? (
                <PartyNewDocsTable
                  items={selectedStat.items}
                  currentUserProfile={currentUserProfile}
                />
              ) : (
                <Header size="small" color="grey">
                  No new docs
                </Header>
              )
            ) : selectedStat.transactions.length > 0 ? (
              <PartyTransactionsTable
                transactions={selectedStat.transactions}
                title={selectedStat.label}
                showClosingDate={selectedStat.showClosingDate}
                showNeedsSignatures={selectedStat.showNeedsSignatures !== false}
              />
            ) : (
              <Header size="small" color="grey">
                No transactions in {selectedStat.label}
              </Header>
            )}
          </Grid.Column>
        )}
      </Grid>
    </div>
  );
}
